/**
 * Editor Store
 *
 * Zustand store for managing the offer editor state.
 */
import { create } from 'zustand';
import { OfferContext } from '../types/editor';

interface EditorState {
  // Content
  content: string;
  originalContent: string;
  isDirty: boolean;

  // Cursor and selection
  cursorPosition: number;
  selection: { from: number; to: number } | null;
  selectedText: string;

  // View state
  showPreview: boolean;
  previewMode: 'split' | 'preview' | 'edit';

  // AI state
  isAILoading: boolean;
  aiStreamContent: string;

  // Context for variable interpolation
  offerContext: OfferContext | null;

  // History for undo/redo
  history: string[];
  historyIndex: number;

  // Actions
  setContent: (content: string) => void;
  setOriginalContent: (content: string) => void;
  setCursorPosition: (position: number) => void;
  setSelection: (selection: { from: number; to: number } | null) => void;
  setSelectedText: (text: string) => void;
  setShowPreview: (show: boolean) => void;
  setPreviewMode: (mode: 'split' | 'preview' | 'edit') => void;
  setOfferContext: (context: OfferContext | null) => void;
  setIsAILoading: (loading: boolean) => void;
  setAIStreamContent: (content: string) => void;

  // Content manipulation
  insertAtCursor: (text: string) => void;
  replaceSelection: (text: string) => void;
  wrapSelection: (before: string, after: string) => void;

  // History
  pushHistory: () => void;
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;

  // State management
  markAsSaved: () => void;
  reset: () => void;
}

const MAX_HISTORY = 50;

export const useEditorStore = create<EditorState>((set, get) => ({
  // Initial state
  content: '',
  originalContent: '',
  isDirty: false,
  cursorPosition: 0,
  selection: null,
  selectedText: '',
  showPreview: true,
  previewMode: 'split',
  isAILoading: false,
  aiStreamContent: '',
  offerContext: null,
  history: [],
  historyIndex: -1,

  // Setters
  setContent: (content) => set((state) => ({
    content,
    isDirty: content !== state.originalContent,
  })),

  setOriginalContent: (originalContent) => set({
    originalContent,
    content: originalContent,
    isDirty: false,
  }),

  setCursorPosition: (cursorPosition) => set({ cursorPosition }),

  setSelection: (selection) => set({ selection }),

  setSelectedText: (selectedText) => set({ selectedText }),

  setShowPreview: (showPreview) => set({ showPreview }),

  setPreviewMode: (previewMode) => set({ previewMode }),

  setOfferContext: (offerContext) => set({ offerContext }),

  setIsAILoading: (isAILoading) => set({ isAILoading }),

  setAIStreamContent: (aiStreamContent) => set({ aiStreamContent }),

  // Content manipulation
  insertAtCursor: (text) => {
    const state = get();
    const { content, cursorPosition } = state;

    // Push to history before modification
    state.pushHistory();

    const newContent =
      content.slice(0, cursorPosition) +
      text +
      content.slice(cursorPosition);

    set({
      content: newContent,
      cursorPosition: cursorPosition + text.length,
      isDirty: true,
    });
  },

  replaceSelection: (text) => {
    const state = get();
    const { content, selection } = state;

    if (!selection) {
      state.insertAtCursor(text);
      return;
    }

    // Push to history before modification
    state.pushHistory();

    const newContent =
      content.slice(0, selection.from) +
      text +
      content.slice(selection.to);

    set({
      content: newContent,
      cursorPosition: selection.from + text.length,
      selection: null,
      selectedText: '',
      isDirty: true,
    });
  },

  wrapSelection: (before, after) => {
    const state = get();
    const { content, selection, selectedText, cursorPosition } = state;

    // Push to history before modification
    state.pushHistory();

    if (selection && selectedText) {
      // Wrap selected text
      const newContent =
        content.slice(0, selection.from) +
        before +
        selectedText +
        after +
        content.slice(selection.to);

      set({
        content: newContent,
        cursorPosition: selection.from + before.length + selectedText.length + after.length,
        selection: null,
        selectedText: '',
        isDirty: true,
      });
    } else {
      // Insert empty wrapper at cursor
      const newContent =
        content.slice(0, cursorPosition) +
        before +
        after +
        content.slice(cursorPosition);

      set({
        content: newContent,
        cursorPosition: cursorPosition + before.length,
        isDirty: true,
      });
    }
  },

  // History management
  pushHistory: () => {
    const state = get();
    const { content, history, historyIndex } = state;

    // Don't push if content is same as current history position
    if (history[historyIndex] === content) {
      return;
    }

    // Remove any redo history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(content);

    // Limit history size
    if (newHistory.length > MAX_HISTORY) {
      newHistory.shift();
    }

    set({
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  },

  undo: () => {
    const state = get();
    const { history, historyIndex, originalContent } = state;

    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      set({
        content: history[newIndex],
        historyIndex: newIndex,
        isDirty: history[newIndex] !== originalContent,
      });
    } else if (historyIndex === 0 && history.length > 0) {
      // Go to original content
      set({
        content: originalContent,
        historyIndex: -1,
        isDirty: false,
      });
    }
  },

  redo: () => {
    const state = get();
    const { history, historyIndex, originalContent } = state;

    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      set({
        content: history[newIndex],
        historyIndex: newIndex,
        isDirty: history[newIndex] !== originalContent,
      });
    }
  },

  canUndo: () => {
    const { historyIndex, content, originalContent } = get();
    return historyIndex > 0 || content !== originalContent;
  },

  canRedo: () => {
    const { history, historyIndex } = get();
    return historyIndex < history.length - 1;
  },

  // State management
  markAsSaved: () => {
    const state = get();
    set({
      originalContent: state.content,
      isDirty: false,
    });
  },

  reset: () => set({
    content: '',
    originalContent: '',
    isDirty: false,
    cursorPosition: 0,
    selection: null,
    selectedText: '',
    showPreview: true,
    previewMode: 'split',
    isAILoading: false,
    aiStreamContent: '',
    offerContext: null,
    history: [],
    historyIndex: -1,
  }),
}));

export default useEditorStore;
